import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  forwardRef, inject, DestroyRef, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {Subject, fromEvent } from 'rxjs';
import { CommentInputValue, UserModel } from '../../models';
import { MutationObserverWrapper, WindowRef } from '../../services';
import { MessageBodySanitizerService } from '../../services/paste-handler/message-body-sanitizer.service';
import { delay, normalizeSpaces, trimHtml } from '../../utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'anecdotes-comment-input',
  templateUrl: './comment-input.component.html',
  styleUrls: ['./comment-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AnecdotesCommentInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AnecdotesCommentInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly detacherSubject = new Subject();
  private mentioningMarkerElement: HTMLElement;
  private allUsers: UserModel[];
  private change: (value: CommentInputValue) => void;
  private touch: () => void; // TODO: To be used later
  private _overlayRef: OverlayRef;
  private currentValue: CommentInputValue;
  private _usersMap: Map<string, UserModel> = new Map<string, UserModel>();

  @ViewChild('input', { static: true })
  private inputElementRef: ElementRef<HTMLDivElement>;
  @ViewChild('stringPlaceholder', { static: true })
  private stringPlaceholderTemplate: TemplateRef<any>;

  private get window(): Window {
    return this.windowRef.getWindow();
  }
  private get overlayRef(): OverlayRef {
    if (!this._overlayRef) {
      this._overlayRef = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });
    }

    return this._overlayRef;
  }

  @ViewChildren('personWrapper')
  private personWrappers: QueryList<ElementRef<HTMLElement>>;

  @HostBinding('class.disabled')
  private isDisabled: boolean;

  @ViewChild('portal', { static: true })
  private popupTemplate: TemplatePortal;

  @Input()
  mentioningMarker = '@';

  @Input()
  editable = true;

  @Input()
  placeholder: TemplateRef<any> | string;

  @Input()
  isSticky = false;

  @Input()
  dropdownWidth = 0; // Default to 0 which means use container width

  @Input()
  stickyOptions: string[] = []; // Array of user IDs that should be sticky at the top

  get usersMap(): Map<string, UserModel> {
    return this._usersMap;
  }

  @Input()
  set usersMap(value: Map<string, UserModel>) {
    this._usersMap = value || new Map<string, UserModel>();
    this.allUsers = Array.from(this._usersMap.values());
  }

  @Output()
  linkClick = new EventEmitter<MouseEvent>();

  @ContentChild('optionTemplate', { static: true })
  optionTemplate: TemplateRef<any>;

  displayedUsers: UserModel[];
  focusedUserIndex = 0;
  isFocused = false;
  popupPosition: 'aboveMark' | 'belowMark';

  get popupWidth(): number {
    const containerWidth = this.hostElement.nativeElement.getBoundingClientRect().width;
    return this.dropdownWidth > 0 ? this.dropdownWidth : containerWidth;
  }

  get value(): CommentInputValue {
    return this.currentValue;
  }

  set value(value: CommentInputValue) {
    this.writeValue(value);
  }

  readonly popupId = 'mentioning-popup' as const;
  readonly mentioningMarkerClass = 'mentioning-marker' as const;
  readonly mentioningPopupClass = 'mentioning-popup' as const;
  readonly placeholderClass = 'placeholder' as const;
  readonly personClass = 'person' as const;
  readonly editableClass = 'editable' as const;

  isPopupOpen: boolean;

  constructor(
    private cd: ChangeDetectorRef,
    private overlay: Overlay,
    private hostElement: ElementRef<HTMLElement>,
    private mutationObserverWrapper: MutationObserverWrapper,
    private windowRef: WindowRef,
    private sanitizer: MessageBodySanitizerService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.mutationObserverWrapper
      .observeAllChanges(this.inputElementRef.nativeElement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleChangedValue());

    fromEvent(document, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onScroll(event));
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.detacherSubject.next(undefined);
  }

  writeValue(inputValue: CommentInputValue): void {
    if (inputValue) {
      inputValue = {
        ...inputValue,
        body: this.sanitizer.sanitizeCommentBody(inputValue.body),
      };
    }

    this.inputElementRef.nativeElement.innerHTML = inputValue?.body ?? '';
    this.currentValue = inputValue ?? null;
    this.cd.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.change = fn;
  }

  registerOnTouched(fn: any): void {
    this.touch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.detectChanges();
  }

  selectPerson(index: number): void {
    if (this.mentioningMarkerElement && (index >= 0 || index <= this.displayedUsers.length - 1)) {
      const user = this.displayedUsers[index];
      const textToInsert = user.name;
      this.insertPersonHtml(textToInsert);
      this.cancelMentioning();
    }
  }

  focus(): void {
    this.inputElementRef.nativeElement.focus();
  }

  keyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape': {
        this.handleEscape();
        break;
      }
      case 'Backspace': {
        this.handleBackspaceAsync(event);
        break;
      }
      case 'Enter': {
        this.handleEnter(event);
        break;
      }
      case this.mentioningMarker: {
        this.handleMark(event);
        break;
      }
      case 'ArrowUp': {
        this.handleArrowUp(event);
        break;
      }
      case 'ArrowDown': {
        this.handleArrowDown(event);
        break;
      }
      case 'ArrowLeft': {
        this.handleArrowLeft(event);
        break;
      }
      case 'ArrowRight': {
        this.handleArrowRight(event);
        break;
      }
    }
  }

  keyUp(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      // delay is used on purpose since the content gets updated after this event get's fired
      delay(0).then(() => {
        this.setDisplayedUsers();

        if (
          (event.code === 'Space' && !this.displayedUsers.length) ||
          (this.getTextFromMentioningMarker().length && !this.getTextFromMentioningMarker().trim())
        ) {
          this.cancelMentioning();
        }

        this.syncPopupPositionAsync();
        this.cd.detectChanges();
      });
    }
  }

  focusIn(_: FocusEvent): void {
    this.isFocused = true;
    this.cd.detectChanges();
  }

  focusOut(_: FocusEvent): void {
    // Set timeout is used here in order to let click on user option work
    // Because focusOut get's fired before click
    this.isFocused = false;
    this.cd.detectChanges();
    delay(300).then(() => this.cancelMentioning());
  }

  paste(event: ClipboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
    const copiedText = event.clipboardData.getData('text');
    const nodesToInsert = this.sanitizer.sanitizeAndCreateNodes(copiedText);
    this.insertNodesForSelection(...nodesToInsert);
  }

  getPlaceholderTemplate(): TemplateRef<any> {
    return typeof this.placeholder === 'string' ? this.stringPlaceholderTemplate : this.placeholder;
  }

  @HostListener('click', ['$event'])
  private async click(event: MouseEvent): Promise<void> {
    if ((event.target as HTMLAnchorElement).tagName === 'A') {
      this.linkClick.emit(event);
    }

    await delay(100);

    if (this.mentioningMarkerElement && event.target !== (this.mentioningMarkerElement as any)) {
      const currentRange = this.window.getSelection().getRangeAt(0);
      this.cancelMentioning();
      this.window.getSelection().removeAllRanges();
      this.window.getSelection().addRange(currentRange);
    }
  }

  private async openMentioningPopupAsync(): Promise<void> {
    this.popupPosition = 'belowMark';

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(this.popupTemplate);
    }

    await this.syncPopupPositionAsync();
    this.isPopupOpen = true;
  }

  private handleChangedValue(): void {
    const body = trimHtml(this.inputElementRef.nativeElement.innerHTML);

    if (body) {
      const mentionedUsersDictionary = Array.from(
        this.inputElementRef.nativeElement.querySelectorAll(`.${this.personClass}`),
      )
        .map((personElement: HTMLElement) => personElement.innerText.substr(1))
        .reduce(
          (result, userName) => ({
            ...result,
            [userName]: this.usersMap.get(userName),
          }),
          {},
        );
      const mentionedUsers: UserModel[] = Object.values(mentionedUsersDictionary);
      const value: CommentInputValue = {
        body,
        mentionedUsers,
      };
      this.triggerChange(value);
    } else {
      this.triggerChange(null);
    }
    this.cd.detectChanges();
  }

  private triggerChange(value: CommentInputValue): void {
    if (this.change) {
      this.change(value);
    }
  }

  private insertPersonHtml(personName: string): void {
    this.selectMark();
    const personSpan = this.window.document.createElement('span');
    personSpan.classList.add(this.personClass);
    personSpan.contentEditable = 'false';
    personSpan.innerText = `${this.mentioningMarker}${personName}`;
    const space = document.createElement('span');
    space.innerHTML = '&nbsp;';
    this.insertNodesForSelection(personSpan, space.childNodes.item(0));
  }

  private handleMark(event: KeyboardEvent): void {
    this.insertMentioningMarker();
    this.setDisplayedUsers();
    this.openMentioningPopupAsync();
    event.preventDefault();
    this.cd.detectChanges();
  }

  private handleEnter(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      this.selectPerson(this.focusedUserIndex);
      this.cancelMentioning();
      event.preventDefault();
    }
  }

  private async handleBackspaceAsync(_: KeyboardEvent): Promise<void> {
    if (this.mentioningMarkerElement?.innerText.length === 1) {
      this.cancelMentioning();
    }

    await delay(0); // delay is used here since updates to the markup happen after keyboard events fire
    if (this.mentioningMarkerElement && !this.doesMarkerBelongsToInput()) {
      this.cancelMentioning();
    }
  }

  private selectMark(): void {
    const sel = this.window.getSelection();
    const range = document.createRange();
    range.selectNode(this.mentioningMarkerElement);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  private handleArrowUp(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      if (this.focusedUserIndex === 0) {
        this.focusedUserIndex = this.displayedUsers.length - 1;
      } else {
        this.focusedUserIndex--;
      }

      this.scrollToPersonIfNotVisible();
      this.cd.detectChanges();
      event.preventDefault();
    }
  }

  private handleArrowDown(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      if (this.focusedUserIndex === this.displayedUsers.length - 1) {
        this.focusedUserIndex = 0;
      } else {
        this.focusedUserIndex++;
      }

      this.scrollToPersonIfNotVisible();
      this.cd.detectChanges();
      event.preventDefault();
    }
  }

  private scrollToPersonIfNotVisible(): void {
    if (this.personWrappers && this.personWrappers.length > this.focusedUserIndex) {
      const personElement = this.personWrappers.get(this.focusedUserIndex).nativeElement;
      const dropdownElement = document.getElementById(this.popupId);

      if (dropdownElement) {
        // Handle sticky option scrolling first
        if (this.isStickyOptionScrolling(dropdownElement)) {
          return;
        }

        const stickyHeight = this.calculateStickyHeadersHeight();
        const dropdownRect = dropdownElement.getBoundingClientRect();
        const personRect = personElement.getBoundingClientRect();

        if (personRect.top < dropdownRect.top + stickyHeight) {
          const scrollAdjustment = dropdownRect.top + stickyHeight - personRect.top;
          dropdownElement.scrollTop -= scrollAdjustment;
          return;
        }

        if (personRect.bottom > dropdownRect.bottom) {
          dropdownElement.scrollTop += (personRect.bottom - dropdownRect.bottom);
          return;
        }
      }
    }
  }

  private isStickyOptionScrolling(dropdownElement: HTMLElement): boolean {
    if (!this.stickyOptions?.length) {
      return false; // No sticky options, no special handling needed
    }

    const isCurrentOptionSticky = this.stickyOptions?.includes(this.displayedUsers[this.focusedUserIndex].name);
    const previousOptionSticky = this.focusedUserIndex > 0 &&
      this.stickyOptions?.includes(this.displayedUsers[this.focusedUserIndex - 1].name);

    if (previousOptionSticky && !isCurrentOptionSticky) {
      dropdownElement.scrollTop = 0;
      return true;
    }

    // If current option is sticky, don't change scroll position
    if (isCurrentOptionSticky) {
      return true;
    }

    return false;
  }

  private calculateStickyHeadersHeight(): number {
    if (!this.stickyOptions || this.stickyOptions.length === 0) {
      return 0;
    }
    const stickyItemHeight = 38;
    return this.stickyOptions.length * stickyItemHeight;
  }

  private handleArrowLeft(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      event.preventDefault();
    }
  }

  private handleArrowRight(event: KeyboardEvent): void {
    if (this.mentioningMarkerElement) {
      event.preventDefault();
    }
  }

  private handleEscape(): void {
    if (this.mentioningMarkerElement) {
      this.cancelMentioning();
    }
  }

  private onScroll(event: Event): void {
    const target = event.target as HTMLElement;

    if (target.id !== this.popupId) {
      this.cancelMentioning();
    }
  }

  private async syncPopupPositionAsync(): Promise<void> {
    await delay(0);

    if (this.mentioningMarkerElement) {
      // using dropdownWidth to set the width of the popup
      if (this.dropdownWidth > 0) {
        const popupElement = document.querySelector(`#${this.popupId}`) as HTMLElement;
        if (popupElement) {
          this.renderer.setAttribute(
            popupElement,
            'style',
            `min-width: ${this.dropdownWidth}px !important; width: ${this.dropdownWidth}px !important; max-width: ${this.dropdownWidth}px !important;`
          );
        }
      }

      const hostRect: DOMRect = this.hostElement.nativeElement.getBoundingClientRect();
      const markRect: DOMRect = this.mentioningMarkerElement.getBoundingClientRect();

      let top: number = markRect.top + markRect.height;
      const overlayBoundingClientRect = this.overlayRef.overlayElement
        .querySelector(`.${this.mentioningPopupClass}`)
        .getBoundingClientRect();

      // if expression is true (the popup can't fit the screen) it means we should display popup above the mark
      if (this.popupPosition === 'aboveMark' || top + overlayBoundingClientRect.height > this.window.innerHeight) {
        top = markRect.top - overlayBoundingClientRect.height;
        this.popupPosition = 'aboveMark';
      }

      this.overlayRef.updatePositionStrategy(
        this.overlay.position().global().top(`${top}px`).left(`${hostRect.left}px`),
      );
    }
  }

  /** Cleanups all resources related to mentioning. */
  private cancelMentioning(): void {
    this.focusedUserIndex = 0;
    if (this.mentioningMarkerElement) {
      this.closeMentioningPopup();

      if (this.doesMarkerBelongsToInput()) {
        this.selectMark();
        this.insertNodesForSelection(document.createTextNode(this.mentioningMarkerElement.innerText));
      }

      delete this.mentioningMarkerElement;
    }
  }

  private closeMentioningPopup(): void {
    this.overlayRef.detach();
    delete this.popupPosition;
    this.isPopupOpen = false;
  }

  private setDisplayedUsers(): void {
    if (this.mentioningMarkerElement) {
      const inputtedText = this.getTextFromMentioningMarker();
      let filteredUsers: UserModel[];

      if (inputtedText) {
        filteredUsers = this.allUsers.filter((user) =>
          user.name.toLowerCase().startsWith(inputtedText.toLowerCase()),
        );
      } else {
        filteredUsers = [...this.allUsers];
      }

      // if we have sticky options, sort the users so sticky ones appear at the top
      if (this.stickyOptions && this.stickyOptions.length > 0) {
        // Split into sticky and non-sticky users
        const stickyUsers = filteredUsers.filter((user) => this.stickyOptions.includes(user.name));
        const nonStickyUsers = filteredUsers.filter((user) => !this.stickyOptions.includes(user.name));

        // Combine them with sticky users first
        this.displayedUsers = [...stickyUsers, ...nonStickyUsers];
      } else {
        this.displayedUsers = filteredUsers;
      }
    }
  }

  /** Inserts html element instead of mark */
  private insertMentioningMarker(): void {
    const mentioningMarkerElement = document.createElement('span');
    mentioningMarkerElement.classList.add(this.mentioningMarkerClass);
    mentioningMarkerElement.innerText = this.mentioningMarker;
    this.insertNodesForSelection(mentioningMarkerElement);
    this.mentioningMarkerElement = mentioningMarkerElement;
  }

  private insertNodesForSelection(...nodesToInsert: Node[]): void {
    const sel: Selection = this.window.getSelection();

    if (sel.rangeCount) {
      sel.deleteFromDocument();
      nodesToInsert.forEach((nodeToInsert) => {
        const range: Range = sel.getRangeAt(0);
        range.collapse(true);
        range.insertNode(nodeToInsert);
        range.setStartAfter(nodeToInsert);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      });
    }
  }

  private doesMarkerBelongsToInput(): boolean {
    return this.mentioningMarkerElement.parentNode === this.inputElementRef.nativeElement;
  }

  private getTextFromMentioningMarker(): string {
    return this.mentioningMarkerElement?.innerText
      ? normalizeSpaces(this.mentioningMarkerElement?.innerText).substr(1) // substr removes mark from the string (@)
      : '';
  }

  handlePersonClick(event: MouseEvent, index: number): void {
    event.stopPropagation();

    this.selectPerson(index);
  }
}
